/*
  The "Graded in" rows, as the pop report draws them (docs/specs/pop-report-followups.md
  D15, R15b). Pure, so the chart only renders.

  The backend keeps gradings with no recorded language apart (`notRecorded`, R5) because
  MR-B's set-language derivation must not read them as anything. On screen they count as
  English (Robert, D15): nearly every unrecorded grading is an English card, and a muted
  "no language recorded" line read as missing data rather than as the answer.
*/

export interface LanguageCount {
  language: string;
  count: number;
}

const ENGLISH = "English";

/* How many languages the game header's compact chart draws before the rest become Other. */
export const COMPACT_LIMIT = 3;

/*
  The rows with `notRecorded` added to English (an English row is inserted when the
  backend sent none), sorted by count, most first. The sort is stable, so equal counts keep
  the backend's order. A new array; the input is left alone.
*/
export const foldUnrecordedIntoEnglish = (
  languages: readonly LanguageCount[],
  notRecorded: number
): LanguageCount[] => {
  const rows = languages.map((row) => ({ ...row }));
  if (notRecorded > 0) {
    const english = rows.find((row) => row.language === ENGLISH);
    if (english) {
      english.count += notRecorded;
    } else {
      rows.push({ language: ENGLISH, count: notRecorded });
    }
  }
  return rows.sort((a, b) => b.count - a.count);
};

/*
  The first `limit` rows, and the rest summed (0 when there is no rest). A `highlight`
  below the top rows is drawn too, as one more row after them and out of Other, so the
  toggle's language always has its bar (D9); one with no row changes nothing.
*/
export const compactLanguages = (
  rows: readonly LanguageCount[],
  limit: number = COMPACT_LIMIT,
  highlight: string | null = null
): { shown: LanguageCount[]; other: number } => {
  const shown = rows.slice(0, limit);
  const rest = rows.slice(limit);
  const lifted = rest.find((row) => row.language === highlight);
  if (lifted) {
    shown.push(lifted);
  }
  return {
    shown,
    other: rest.reduce((sum, row) => (row === lifted ? sum : sum + row.count), 0),
  };
};
